import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import { catchError, distinctUntilChanged, exhaustMap, map, tap } from 'rxjs/operators';

import { OrderPreviewsService } from '@fcom/order';
import { FinnairOrderPreview, FinnairOrderPreviewsResponse } from '@fcom/dapi/api/models';
import { compareAsJson, TzDate } from '@fcom/core';

import { UpcomingTripsActions } from '../actions';
import { PnrWidgetState, UpcomingTripsFetchStatus } from '../../interfaces/store.interface';
import { UpcomingTrip } from '../../interfaces';
import { MAX_SHOWN_TRIPS } from '../../components/upcoming-trips/upcoming-trips.component';

@Injectable()
export class UpcomingTripsEffects {
  fetchUpcomingTrips$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UpcomingTripsActions.fetchTrips.type),
        exhaustMap(() =>
          this.orderPreviewService.getUpcomingTrips().pipe(
            distinctUntilChanged((previousResponse, currentResponse) =>
              compareAsJson(previousResponse, currentResponse)
            ),
            map((response) => this.mapOrderPreviewsToUpcomingTrips(response.orderPreviews, response.locations))
          )
        ),
        tap((upcomingTrips: UpcomingTrip[]) => {
          this.store.dispatch(
            UpcomingTripsActions.setTrips({
              trips: upcomingTrips,
              status: UpcomingTripsFetchStatus.FETCHED,
            })
          );
        }),
        catchError(() => {
          this.store.dispatch(UpcomingTripsActions.setTrips({ trips: [], status: UpcomingTripsFetchStatus.ERROR }));
          return EMPTY;
        })
      ),
    { dispatch: false }
  );

  constructor(
    private readonly actions$: Actions<UpcomingTripsActions.UpcomingTripsActionsUnion>,
    private orderPreviewService: OrderPreviewsService,
    private store: Store<PnrWidgetState>
  ) {}

  private mapOrderPreviewsToUpcomingTrips(
    orderPreviews: FinnairOrderPreview[],
    locations: FinnairOrderPreviewsResponse['locations']
  ): UpcomingTrip[] {
    return (
      orderPreviews?.slice(0, MAX_SHOWN_TRIPS).map((orderPreview) => {
        return {
          bookingReference: orderPreview.id,
          lastName: orderPreview.lastName,
          departure: {
            location: locations[orderPreview.bounds[0].departure.locationCode],
            date: TzDate.of(orderPreview.bounds[0].departure.dateTime).toLocalDate(),
          },
          arrival: {
            location: locations[orderPreview.bounds[0].arrival.locationCode],
            date: TzDate.of(orderPreview.bounds[0].arrival.dateTime).toLocalDate(),
          },
          remainingTrips: orderPreview.bounds.length - 1,
        };
      }) || []
    );
  }
}
